// RunnerMetricsChart: live CPU + memory usage for a single runner.
// Pipelines-list page subscribes to a per-runner pubsub topic and
// pushes one `runnerMetricsSample` event per tick targeted by
// `runner_id`. Shares all rendering helpers with `JobMetricsChart` —
// the only differences are the event names and a `runner_id` filter
// so a sample for runner A doesn't paint into runner B's chart.

import type { LiveViewHook, CiPushEventWire } from "../types";
import {
  parseRgbTriple,
  noteMetricsSampleForScroll,
  resetMetricsScroll,
  ringPush,
  RING_CAP,
  updateMetricsScrollProgress,
  type ChartPoint,
  type MetricsSample,
  type Rgb,
} from "./job-metrics-chart";
import { cssVar } from "./theme";
import { buildGradientStops } from "./metrics-axes";
import { redrawMetrics } from "./metrics-render";
import {
  DataAttr,
  readPositiveNumberData,
  requireHookCanvas,
} from "./hook-dom-contract";

// `RunnerMetricsPayload` is dox-owned (`domain/ci/runner.dox`); the
// Elixir producer at `lib/operation_web/live/pipeline_live/index.ex`
// builds the same record. The `runnerMetricsLoad` payload is still
// snake_case (`runner_id`, `samples: [{cpu_us, mem_bytes, ts_ms}]`)
// because paradox rejects single-field `type` records, so wrapping
// the load payload in dox requires more than one field. Until then,
// the load wire shape stays inline below; the per-sample push has
// been rotated.
import type { RunnerMetricsPayload } from "../../../../.dox/ci";

interface RunnerLoadWireSample {
  cpu_us: number;
  mem_bytes: number;
  ts_ms: number;
}

const projectRunnerSample = (p: RunnerMetricsPayload): MetricsSample => ({
  cpuUsageUs: p.cpuUs,
  memoryBytes: p.memBytes,
  tsMs: p.tsMs,
});

const projectLoadWireSample = (p: RunnerLoadWireSample): MetricsSample => ({
  cpuUsageUs: p.cpu_us,
  memoryBytes: p.mem_bytes,
  tsMs: p.ts_ms,
});

interface RunnerMetricsChartHook extends LiveViewHook<CiPushEventWire> {
  _runnerId: number;
  _samples: MetricsSample[];
  _chart: ChartPoint[];
  _lastChart?: ChartPoint[] | undefined;
  _lastCpuMax?: number | undefined;
  _lastMemMax?: number | undefined;
  _cores: number;
  _ramBytes: number;
  _ramps?: { cpu: Rgb[]; mem: Rgb[] } | undefined;
  _canvas?: HTMLCanvasElement | undefined;
  _ctx?: CanvasRenderingContext2D | undefined;
  _w: number;
  _h: number;
  _rafId?: number | undefined;
  _ro?: ResizeObserver | undefined;
  _lastSampleTsMs?: number | undefined;
  _scrollStartedAtMs?: number | undefined;
  _scrollDurationMs?: number | undefined;
  _scrollStartOffsetSlots?: number | undefined;
  _scrollOffsetSlots?: number | undefined;
  redraw(this: RunnerMetricsChartHook): void;
  scheduleRedraw(this: RunnerMetricsChartHook): void;
}

export const parseIntAttr = (el: HTMLElement, key: string): number => {
  const raw = el.dataset[key];
  if (!raw) return 0;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

export const buildRamps = (): { cpu: Rgb[]; mem: Rgb[] } => {
  const stops = buildGradientStops({
    info: parseRgbTriple(cssVar("--c-info")),
    warning: parseRgbTriple(cssVar("--c-warning")),
    danger: parseRgbTriple(cssVar("--c-danger")),
  });
  return { cpu: [...stops.cpu], mem: [...stops.mem] };
};

export const RunnerMetricsChart = {
  mounted(this: RunnerMetricsChartHook) {
    this._runnerId = readPositiveNumberData(
      this.el,
      "runnerMetricsChart",
      DataAttr.runner_id,
    );
    this._cores = readPositiveNumberData(
      this.el,
      "runnerMetricsChart",
      DataAttr.cores,
    );
    this._ramBytes = readPositiveNumberData(
      this.el,
      "runnerMetricsChart",
      DataAttr.ram_bytes,
    );
    this._samples = [];
    this._chart = [];
    this._ramps = buildRamps();
    this._rafId = undefined;
    resetMetricsScroll(this);

    const rect = this.el.getBoundingClientRect();
    this._w = rect.width || 240;
    this._h = rect.height || 48;

    const canvas = requireHookCanvas(this.el, "runnerMetricsChart");
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    this._canvas = canvas;
    this._ctx = ctx;

    this._ro = new ResizeObserver(() => {
      const r = this.el.getBoundingClientRect();
      this._w = r.width || this._w;
      this._h = r.height || this._h;
      this._lastChart = undefined;
      this.scheduleRedraw();
    });
    this._ro.observe(this.el);

    this.handleEvent("runnerMetricsSample", (raw: unknown) => {
      // The server batches samples from all runners into a single push_event
      // per 100 ms flush cycle. `batch` is an array of RunnerMetricsPayload;
      // each hook instance filters to its own runner_id.
      // Also accepts a single RunnerMetricsPayload directly (for backward compat).
      const payload = raw as
        | { batch?: ReadonlyArray<RunnerMetricsPayload> }
        | RunnerMetricsPayload;
      const samples: ReadonlyArray<RunnerMetricsPayload> = Array.isArray(
        (payload as { batch?: ReadonlyArray<RunnerMetricsPayload> }).batch,
      )
        ? (payload as { batch: ReadonlyArray<RunnerMetricsPayload> }).batch
        : "runnerId" in payload
          ? [payload]
          : [];
      let didUpdate = false;
      for (const p of samples) {
        if (p.runnerId !== this._runnerId) continue;
        const s = projectRunnerSample(p);
        this._samples = ringPush(this._samples, s, RING_CAP);
        noteMetricsSampleForScroll(this, s.tsMs);
        didUpdate = true;
      }
      if (didUpdate) this.scheduleRedraw();
    });

    this.handleEvent("runnerMetricsLoad", (raw: unknown) => {
      const payload = raw as {
        runner_id?: number;
        samples?: ReadonlyArray<RunnerLoadWireSample>;
      };
      if (payload.runner_id !== this._runnerId) return;
      const wire: ReadonlyArray<RunnerLoadWireSample> = Array.isArray(
        payload.samples,
      )
        ? (payload.samples as ReadonlyArray<RunnerLoadWireSample>)
        : [];
      const projected: MetricsSample[] = wire.map(projectLoadWireSample);
      this._samples =
        projected.length > RING_CAP
          ? projected.slice(projected.length - RING_CAP)
          : projected;
      this._lastChart = undefined;
      resetMetricsScroll(this);
      this.scheduleRedraw();
    });
  },

  scheduleRedraw(this: RunnerMetricsChartHook) {
    if (this._rafId != null) return;
    this._rafId = requestAnimationFrame((nowMs) => {
      this._rafId = undefined;
      const keepAnimating = updateMetricsScrollProgress(this, nowMs);
      this.redraw();
      if (keepAnimating) this.scheduleRedraw();
    });
  },

  redraw(this: RunnerMetricsChartHook) {
    redrawMetrics(this, {
      idBase: this.el.id || `runner-metrics-${this._runnerId}`,
      rampsFallback: buildRamps,
    });
  },

  destroyed(this: RunnerMetricsChartHook) {
    if (this._rafId != null) {
      cancelAnimationFrame(this._rafId);
      this._rafId = undefined;
    }
    this._ro?.disconnect();
    this._ro = undefined;
    this._canvas = undefined;
    this._ctx = undefined;
    this._ramps = undefined;
    this._samples = [];
    this._chart = [];
    this._lastChart = undefined;
    this._lastCpuMax = undefined;
    this._lastMemMax = undefined;
    resetMetricsScroll(this);
  },
};
